import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Users, MailOpen, KanbanSquare, ArrowRight, CircleCheck,
} from 'lucide-react';
import WaitlistForm from '../components/WaitlistForm';

/* ----------------------------------------------------------------------------
 * For Recruiters — the paid side of the two-sided platform. Skip the flood:
 * start with a pool of motivated, intent-scored tech candidates, reach out
 * warm, run a lightweight pipeline. Blue brand, editorial serif headings,
 * hairline borders, subtle motion. Matches Home.js / Product.js / Pool.js.
 * -------------------------------------------------------------------------- */

const BLUE = '#2563EB';

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
};

const PROBLEMS = [
  {
    stat: '300+',
    label: 'applications per role',
    desc: 'Most are noise. Screening them is a full-time job you never asked for.',
  },
  {
    stat: 'Cold',
    label: 'outreach lands in spam',
    desc: 'Blasting passive profiles who were never looking gets you ignored.',
  },
  {
    stat: '$25k',
    label: 'sourcing tools',
    desc: 'Enterprise contracts to dig through stale, scraped résumés.',
  },
];

const FIX = [
  {
    icon: Users,
    title: 'A pool of motivated candidates',
    desc: "Search intent-scored tech candidates by role, seniority, and location — ranked by who's actually open, not who merely exists.",
  },
  {
    icon: MailOpen,
    title: 'Warm outreach',
    desc: 'Reach people who already raised their hand. Your message lands as a welcome reply, not another cold blast in the spam folder.',
  },
  {
    icon: KanbanSquare,
    title: 'A lightweight pipeline',
    desc: 'Move candidates from first contact to hire in a simple, fast pipeline — no $25k tool, no bloat.',
  },
];

function ForRecruiters() {
  return (
    <div className="bg-[#F8FAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-white to-[#F8FAFC] dark:from-gray-950 dark:to-gray-950">
        <div className="mx-auto max-w-4xl px-6 py-20 text-center lg:px-8 lg:py-28">
          <motion.div {...reveal} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.05em] text-gray-500">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: BLUE }} />
            For recruiters
          </motion.div>
          <motion.h1 {...reveal} transition={{ duration: 0.55, delay: 0.05 }}
            className="mt-6 font-serif text-5xl font-semibold leading-[1.05] tracking-tight text-gray-900 dark:text-white sm:text-6xl">
            Skip the flood. Start with candidates who actually want your role.
          </motion.h1>
          <motion.p {...reveal} transition={{ duration: 0.55, delay: 0.1 }}
            className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
            AutoHire gives you a pool of motivated, intent-scored tech candidates — plus warm outreach and a lightweight pipeline to take them from hello to hire.
          </motion.p>
          <motion.div {...reveal} transition={{ duration: 0.55, delay: 0.16 }} className="mt-9 flex justify-center">
            <Link to="/register" className="inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-base font-semibold text-white transition-colors"
              style={{ background: BLUE }} onMouseEnter={(e) => (e.currentTarget.style.background = '#1D4FD8')} onMouseLeave={(e) => (e.currentTarget.style.background = BLUE)}>
              See candidates for your role <ArrowRight size={17} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ===== THE PROBLEM ===== */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <motion.div {...reveal} transition={{ duration: 0.5 }} className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: BLUE }}>The problem</div>
          <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Sourcing today is noise, cold blasts, and big contracts.
          </h2>
        </motion.div>
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {PROBLEMS.map((p, i) => (
            <motion.div key={p.label} {...reveal} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-7">
              <div className="font-serif text-4xl font-semibold text-gray-900 dark:text-white">{p.stat}</div>
              <div className="mt-1 text-sm font-semibold text-gray-700 dark:text-gray-200">{p.label}</div>
              <p className="mt-3 text-sm leading-relaxed text-gray-500 dark:text-gray-400">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ===== THE FIX ===== */}
      <section className="border-y border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <motion.div {...reveal} transition={{ duration: 0.5 }} className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: BLUE }}>The fix</div>
            <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Start warm. Stay lightweight.
            </h2>
          </motion.div>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {FIX.map((f, i) => (
              <motion.div key={f.title} {...reveal} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-[#F8FAFC] dark:bg-gray-900 p-7">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: '#EFF4FE', color: BLUE }}>
                  <f.icon size={20} strokeWidth={1.8} />
                </div>
                <h3 className="mt-5 font-serif text-lg font-semibold text-gray-900 dark:text-white">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-500 dark:text-gray-400">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== DIFFERENTIATOR ===== */}
      <section className="mx-auto max-w-4xl px-6 py-24 text-center lg:px-8">
        <motion.div {...reveal} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: BLUE }}>
          <CircleCheck size={15} /> Why it's different
        </motion.div>
        <motion.p {...reveal} transition={{ duration: 0.55, delay: 0.06 }}
          className="mt-6 font-serif text-3xl font-medium leading-relaxed text-gray-900 dark:text-white sm:text-4xl">
          Gem and hireEZ tell you who exists. We show you who's open, today.
        </motion.p>
      </section>

      {/* ===== RECRUITER WAITLIST ===== */}
      <section className="border-y border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
        <div className="mx-auto max-w-3xl px-6 py-20 text-center lg:px-8">
          <motion.div {...reveal} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: BLUE }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: BLUE }} />
            Early access
          </motion.div>
          <motion.h2 {...reveal} transition={{ duration: 0.55, delay: 0.05 }}
            className="mt-5 font-serif text-3xl font-semibold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
            Join the recruiter waitlist
          </motion.h2>
          <motion.p {...reveal} transition={{ duration: 0.55, delay: 0.1 }}
            className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-gray-600 dark:text-gray-300">
            We're onboarding hiring teams in batches. Add your email and we'll reach out as soon as a spot opens up.
          </motion.p>
          <motion.div {...reveal} transition={{ duration: 0.55, delay: 0.16 }} className="mx-auto mt-8 max-w-md">
            <WaitlistForm audience="recruiter" />
          </motion.div>
        </div>
      </section>

      {/* ===== PRICING TEASER / BOTTOM CTA ===== */}
      <section className="bg-[#0F172A]">
        <div className="mx-auto max-w-5xl px-6 py-24 text-center lg:px-8">
          <motion.div {...reveal} transition={{ duration: 0.55 }}>
            <h2 className="font-serif text-4xl font-semibold leading-tight text-white sm:text-5xl">
              Pay for intent, not access to a database.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-gray-300">
              Simple pricing built for startups hiring engineers — not a $25k enterprise contract.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/register" className="inline-flex items-center justify-center gap-2 rounded-lg px-7 py-3.5 text-base font-semibold text-white" style={{ background: BLUE }}>
                See candidates for your role <ArrowRight size={17} />
              </Link>
              <Link to="/pricing" className="inline-flex items-center justify-center rounded-lg border border-white/20 bg-transparent px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-white/10">
                View pricing
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default ForRecruiters;
